import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IEventoGenerico } from 'modelos/src';
import { ItemCategoria } from '../lista-categoria/lista-categoria.component';

/**
 * Eventos del viaje, con filtro por título (chips). Mapea los eventos a la
 * categoría genérica; el armazón y el estilo son de app-lista-categoria.
 *
 * Presentacional: no busca ni conoce el estado del mapa, sólo emite el evento
 * elegido.
 */
@Component({
  selector: 'app-lista-eventos',
  templateUrl: './lista-eventos.component.html',
  styleUrl: './lista-eventos.component.scss',
  standalone: false,
})
export class ListaEventosComponent {
  private fecha = new DatePipe('es-AR');

  @Input() set eventos(value: IEventoGenerico[] | undefined) {
    this._eventos = value ?? [];
    this.actualizarTitulos();
    this.aplicarFiltroTitulos();
  }
  get eventos(): IEventoGenerico[] {
    return this._eventos;
  }
  private _eventos: IEventoGenerico[] = [];

  /** Evento resaltado (el seleccionado en el mapa). */
  @Input() set seleccionado(value: IEventoGenerico | undefined) {
    this._seleccionado = value;
    this.aplicarFiltroTitulos();
  }
  get seleccionado(): IEventoGenerico | undefined {
    return this._seleccionado;
  }
  private _seleccionado?: IEventoGenerico;

  /** Los eventos se están calculando: la cabecera muestra spinner en vez del total. */
  @Input() cargando = false;

  @Output() seleccionar = new EventEmitter<IEventoGenerico>();

  public items: ItemCategoria<IEventoGenerico>[] = [];
  public titulosDisponibles: string[] = [];
  public titulosSeleccionados = new Set<string>();

  // El Output de la categoría no es genérico (los templates no propagan el
  // tipo), así que el dato vuelve como unknown y se castea acá.
  public verEnMapa(item: ItemCategoria): void {
    const evento = item.dato as IEventoGenerico | undefined;
    if (evento) this.seleccionar.emit(evento);
  }

  public getTitulo(evento: IEventoGenerico): string {
    const valores = evento.valores as any;
    return valores?.titulo || evento.categoria || evento.tipoEvento || '';
  }

  public toggleTitulo(titulo: string): void {
    if (this.titulosSeleccionados.has(titulo)) {
      this.titulosSeleccionados.delete(titulo);
    } else {
      this.titulosSeleccionados.add(titulo);
    }
    this.aplicarFiltroTitulos();
  }

  public toggleTodasCategorias(): void {
    if (this.titulosSeleccionados.size === this.titulosDisponibles.length) {
      this.titulosSeleccionados.clear();
    } else {
      this.titulosSeleccionados = new Set(this.titulosDisponibles);
    }
    this.aplicarFiltroTitulos();
  }

  public getValoresDetalle(
    evento: IEventoGenerico,
  ): { label: string; valor: string }[] {
    const detalles: { label: string; valor: string }[] = [];
    const valores = evento.valores as any;

    const categoria = evento.categoria || evento.tipoEvento;
    if (categoria) {
      detalles.push({ label: 'Categoría', valor: categoria });
    }

    if (valores?.mensaje && valores.mensaje !== valores.titulo) {
      detalles.push({ label: 'Mensaje', valor: valores.mensaje });
    }
    if (valores?.direccion) {
      detalles.push({ label: 'Dirección', valor: valores.direccion });
    }
    if (valores?.codigoTracker) {
      detalles.push({ label: 'Código tracker', valor: valores.codigoTracker });
    }

    return detalles;
  }

  public getEventoIcon(evento: IEventoGenerico): string {
    switch (evento.tipoEvento) {
      case 'Evento Vehiculo':
        return 'directions_car';
      case 'Evento Tracker':
        return 'gps_fixed';
      case 'Evento Activo':
        return 'devices';
      case 'Evento Alarma':
        return 'notification_important';
      case 'Evento Colectivo':
        return 'directions_bus';
      default:
        return 'event';
    }
  }

  public getEstadoColor(estado?: string): string {
    switch (estado) {
      case 'Sin Tratamiento':
        return '#f44336';
      case 'Pendiente':
        return '#ff9800';
      case 'En Atención':
        return '#2196f3';
      case 'En Espera':
        return '#9c27b0';
      case 'Liberada':
        return '#4caf50';
      case 'Finalizada':
        return '#607d8b';
      default:
        return '#757575';
    }
  }

  private actualizarTitulos(): void {
    const titulos = new Set<string>();
    for (const evento of this._eventos) {
      const titulo = this.getTitulo(evento);
      if (titulo) titulos.add(titulo);
    }
    const nuevosTitulos = Array.from(titulos).sort();

    const titulosIguales =
      this.titulosDisponibles.length === nuevosTitulos.length &&
      this.titulosDisponibles.every((t, i) => t === nuevosTitulos[i]);

    if (!titulosIguales) {
      this.titulosDisponibles = nuevosTitulos;
      this.titulosSeleccionados = new Set(this.titulosDisponibles);
    }
  }

  private aplicarFiltroTitulos(): void {
    const todos =
      this.titulosSeleccionados.size === 0 ||
      this.titulosSeleccionados.size === this.titulosDisponibles.length;

    const filtrados = todos
      ? this._eventos
      : this._eventos.filter((e) => {
          const titulo = this.getTitulo(e);
          return titulo ? this.titulosSeleccionados.has(titulo) : false;
        });

    this.items = filtrados.map((evento) => ({
      id: evento._id ?? `${evento.fechaCreacion}`,
      titulo:
        this.fecha.transform(evento.fechaCreacion, 'dd/MM/yyyy HH:mm') ?? '',
      descripcion: this.getTitulo(evento),
      colorDescripcion: this.getEstadoColor(evento.estado),
      detalles: [
        ...(evento.estado
          ? [{ label: 'Estado', valor: evento.estado, icono: 'info' }]
          : []),
        ...this.getValoresDetalle(evento).map((d) => ({
          label: d.label,
          valor: d.valor,
          icono: 'description',
        })),
      ],
      seleccionado: this._seleccionado?._id === evento._id,
      dato: evento,
    }));
  }
}
